import React from "react"
import { Link } from "gatsby"
import { StaticImage } from "gatsby-plugin-image"

import { routes } from "@/utils/routes"
import clsx from "clsx"

type Props = {
  removeMargin?: boolean
}

const Footer: React.FC<Props> = ({ removeMargin }) => {
  return (
    <footer className={clsx("relative pb-8 bg-[#131312] text-white border-t border-primary", { "mt-8": !removeMargin })}>
      <div className="container px-4 py-10 pb-4">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Column - Logo and Contact Info */}
          <div className="flex-1">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="w-full max-w-[108px]">
                <img src="/images/Logo/Gilroy-logo.png" alt="RBAI Foundation" className="w-full h-auto" />
              </div>
              <div className="flex-1">
                <p className="text-[#bfbfbf] font-gilroy font-light mb-0">
                  College Square East,<br />
                  Belfast,<br />
                  Co. Antrim,<br />
                  BT1 6DL
                </p>
                <div className="text-[#bfbfbf] font-gilroy font-light mb-2">
                  <p className="font-gilroy font-light">
                    T: <a className="text-white hover:text-primary font-gilroy" href="tel:02890 240461">028 90 240461</a>
                  </p>
                  <p className="font-gilroy font-light">
                    E: <a className="text-white hover:text-primary font-gilroy" href="mailto:p.anderson@rbai.org.uk">p.anderson@rbai.org.uk</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Column - Donate Button Only */}
          <div className="flex-1 lg:max-w-[300px] flex items-center justify-center">
            <Link to={routes.donate()} className="inline-block bg-primary text-dark px-8 py-4 rounded-sm font-gilroy font-bold text-xl hover:bg-[#d9a01d] transition-colors text-center uppercase">
              DONATE NOW
            </Link>
          </div>
        </div>
      </div>

      <div>
        <div className="container px-2 py-3">
          <div className="flex gap-2 md:gap-4 flex-wrap items-center justify-between text-xs leading-relaxed">
            <div className="flex gap-2 md:gap-4 flex-wrap items-center">
              <span className="font-gilroy font-light">© 2026</span>
              <span className="font-gilroy font-light">The Royal Belfast Academical Institution is registered with the Charity Commission for Northern Ireland (Charity Number 108024)</span>
            </div>
            <span className="flex-shrink-0 font-gilroy font-light">
              Powered by <a href="https://payhere.co" target="_blank" className="font-gilroy">Payhere</a>
            </span>
          </div>
        </div>
      </div>

      {/* Modal retained as-is */}
      <div
        className="modal fade hidden"
        id="exampleModalLong"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="exampleModalLongTitle"
        aria-hidden="true"
      >
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLongTitle">
                Paviour Pledge - Terms and Conditions
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">&nbsp;</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="inner_body_txt">
                <div>
                  In return for pledging £800 in support of The Royal Belfast
                  Academical Institution (RBAI), alumni, parents, staff and
                  friends of the School are invited to have their name and years
                  in attendance at RBAI inscribed on a paviour, for example:
                </div>
                <ul>
                  <li>Line 1: ‘Peter Nicol’</li>
                  <li>Line 2 ‘RBAI 1969 – 76’</li>
                </ul>
                <div>
                  <p>
                    A pledge of £800 entitles the donor to a single paviour with
                    2 lines of text. A pledge of £1,600 entitles the donor to a
                    double paviour with 4 lines of text. The maximum number of
                    characters on each line, including spaces, is restricted to
                    16.
                  </p>
                </div>
                <div>
                  <p>
                    Inscriptions must be the names of individuals only - not
                    companies, organisations, clubs or societies.
                  </p>
                </div>
                <div>
                  <p>
                    RBAI reserves the right to approve all lettering and to
                    refuse any application considered unsuitable. In such cases
                    RBAI has no obligation to cite a reason and a full refund
                    will be offered.
                  </p>
                </div>
                <div>
                  <p>
                    Engraving of the paviours will be done by a specialist
                    engraver but names will not be painted - so as to retain the
                    historical look and feel of the School environment.
                  </p>
                </div>
                <div>
                  <p>
                    Donors do not receive a paviour. It will be installed in the
                    School quad and all paviours remain the property of RBAI.
                  </p>
                </div>
                <div>
                  <p>
                    RBAI will have complete control over the location and
                    installation date of the paviours.
                  </p>
                </div>
                <div>
                  <p>
                    The first paviours are scheduled to be installed in the
                    summer of 2021.
                  </p>
                </div>
                <div>
                  <p>
                    RBAI guarantees to maintain the walkway in perpetuity,
                    however the paviours may become worn or damaged over time
                    owing to natural causes. No liability can be accepted for
                    this by RBAI.
                  </p>
                </div>
                <div>
                  <p>
                    Every effort will be made to keep the paviours in the chosen
                    location, however RBAI reserves the right to move or
                    relocate the paviours without liability.
                  </p>
                </div>
                <div>
                  <p>
                    Any amendments to the name provided on the order form must
                    be made within 14 days of receipt of the confirmation
                    letter; thereafter RBAI accepts no responsibility for
                    changed or incorrect names.
                  </p>
                </div>
                <div>
                  <p>Paviours may differ in colour from those illustrated.</p>
                </div>
                <div>
                  <p>
                    For any queries, please contact: Peter Anderson, RBAI
                    Foundation Director. T: <a href="tel:028 9024 0461">028 9024 0461</a>, E: <a href="mailto:p.anderson@rbai.org.uk"> p.anderson@rbai.org.uk</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
